import { DirectiveOptions, DirectiveBinding } from 'vue/types/options';
import { VNode } from "vue";
import './dragAndDrop.scss';

let callback: Function = null;
const draggableMap: WeakMap<HTMLElement, DirectiveBinding> = new WeakMap<HTMLElement, DirectiveBinding>();
const droppableMap: WeakMap<HTMLElement, DirectiveBinding> = new WeakMap<HTMLElement, DirectiveBinding>();

function getText(binding: DirectiveBinding) {
	const keys = [
		"dragAndDrop",
		...Object.keys(binding.modifiers)
	];
	return keys.join("_");
}

const dragStart = (el: HTMLElement, vnode: VNode) => (e: DragEvent) => {
	// see: https://medium.com/@reiberdatschi/common-pitfalls-with-html5-drag-n-drop-api-9f011a09ee6c for examples
	// also: https://caniuse.com/#feat=dragndrop
	if (!el.draggable) {
		e.preventDefault();
		return;
	}

	//console.log('dragstart', e, binding);
	e.stopPropagation();

	const binding = draggableMap.get(el);

	// Firefox requires you to setData on the dataTransfer
	// Use "text" instead of "text/plain" (if applicable)
	e.dataTransfer.setData('text', getText(binding));

	callback = (value) => {
		if (typeof binding.value == "function")
			binding.value(binding.arg, value);
	}

	el.classList.add('draggable--dragging');
	el.dispatchEvent(new Event("draggable:start"));
};

const dragEnd = (el: HTMLElement, vnode: VNode) => (e: DragEvent) => {
	//console.log('dragend', e);
	el.classList.remove('draggable--dragging');
	el.dispatchEvent(new Event("draggable:end"));
};

const draggable: DirectiveOptions = {
	bind: (el, binding, vnode, oldVnode) => {
		draggableMap.set(el, binding);
		el.classList.add('draggable');
		el.addEventListener('dragstart', dragStart(el, vnode));
		el.addEventListener('dragend', dragEnd(el, vnode));
	},
	inserted: (el, binding, vnode, oldVnode) => {

	},
	update: (el, binding, vnode, oldVnode) => {

	},
	componentUpdated: (el, binding, vnode, oldVnode) => {
		draggableMap.set(el, binding);
	},
	unbind: (el, binding, vnode, oldVnode) => {
		draggableMap.delete(el);
		el.classList.remove('draggable');
		el.removeEventListener('dragstart', dragStart(el, vnode));
		el.removeEventListener('dragend', dragEnd(el, vnode));
	}
};

const dragOver = (el: HTMLElement) => (e: DragEvent) => {
	e.preventDefault();
};

const dragEnter = (el: HTMLElement) => (e: DragEvent) => {
	//console.log("dragEnter", e);
	el.classList.add('droppable--over');
	el.dispatchEvent(new Event("droppable:enter"));
};

const dragLeave = (el: HTMLElement) => (e: DragEvent) => {
	//console.log("dragLeave", e);
	el.classList.remove('droppable--over');
	el.dispatchEvent(new Event("droppable:leave"));
};

const drop = (el: HTMLElement) => (e: DragEvent) => {
	e.preventDefault();
	el.classList.remove('droppable--over');

	const data = e.dataTransfer.getData("text");
	const binding = droppableMap.get(el);
	if (data !== getText(binding)) {
		return false;
	}

	//console.log("drop", e, binding);
	if (callback)
		callback(binding.arg);

	return true;
};

const droppable: DirectiveOptions = {
	bind: (el, binding, vnode, oldVnode) => {
		droppableMap.set(el, binding);
		el.classList.add('droppable');
		el.addEventListener('dragover', dragOver(el));
		el.addEventListener('dragenter', dragEnter(el));
		el.addEventListener('dragleave', dragLeave(el));
		el.addEventListener('drop', drop(el));
	},
	inserted: (el, binding, vnode, oldVnode) => {

	},
	update: (el, binding, vnode, oldVnode) => {

	},
	componentUpdated: (el, binding, vnode, oldVnode) => {
		droppableMap.set(el, binding);
	},
	unbind: (el, binding, vnode, oldVnode) => {
		droppableMap.delete(el);
		el.classList.remove('droppable');
		el.removeEventListener('dragover', dragOver(el));
		el.removeEventListener('dragenter', dragEnter(el));
		el.removeEventListener('dragleave', dragLeave(el));
		el.removeEventListener('drop', drop(el));
	}
};

export {
	draggable,
	droppable
}